// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { consulterRegistreEntreprises, putIdentiteFacturation } from '../services/api';

/**
 * Identité de facturation, demandée AVANT la page de paiement Stripe.
 *
 * Chantier SaaS 1 (facture électronique au 1ᵉʳ/09/2027), décisions de Marc des
 * 22 et 23/09/2026 : professionnels UNIQUEMENT, établis en France ; SIREN
 * obligatoire, vérifié au répertoire officiel qui préremplit raison sociale et
 * adresse ; TVA facultative (un micro-entrepreneur en franchise n'en a pas).
 *
 * ⭐ Des CARTES, pas une case à cocher (modèle Mistral retenu par Marc) : une
 * seule aujourd'hui, « Professionnel ». Le jour où Normascan s'ouvrira aux
 * particuliers (`docs/contexte/ouverture-particuliers.md`), une seconde carte
 * s'ajoute à `PROFILS` sans refaire l'écran.
 *
 * ⚠️ L'écran n'est pas la serrure : le serveur refuse lui-même une session de
 * paiement sans identité complète (409 `identite_requise`).
 *
 * `mode` : `paiement` (le bouton mène au paiement) ou `completer` (bandeau d'un
 * abonné existant : on enregistre, rien d'autre).
 */

const PROFILS = [
  { cle: 'professionnel', titre: 'identite_profil_pro', texte: 'identite_profil_pro_texte' },
];

const CHAMPS = [
  ['raison_sociale', 'identite_raison_sociale', true],
  ['adresse', 'identite_adresse', true],
  ['code_postal', 'identite_code_postal', true],
  ['ville', 'identite_ville', true],
  ['tva_intracom', 'identite_tva', false],
  ['email_facturation', 'identite_email_facturation', false],
];

// Même clé que le serveur (`valider_siren`) : on n'interroge le registre qu'une
// fois le numéro plausible, pour ne pas solliciter l'API publique à chaque chiffre.
const sirenPlausible = (siren) => {
  if (!/^\d{9}$/.test(siren)) return false;
  if (siren === '356000000') return true;           // La Poste, hors Luhn
  let total = 0;
  [...siren].reverse().forEach((c, rang) => {
    let v = Number(c);
    if (rang % 2 === 1) { v *= 2; if (v > 9) v -= 9; }
    total += v;
  });
  return total % 10 === 0;
};

const IdentiteFacturationModal = ({ identite, emailConnexion, mode = 'paiement', onValide, onClose }) => {
  const { t } = useI18n();
  const [profil, setProfil] = useState('professionnel');
  const [valeurs, setValeurs] = useState(() => ({
    siren: identite?.siren || '',
    ...Object.fromEntries(CHAMPS.map(([c]) => [c, identite?.[c] || ''])),
  }));
  const [registre, setRegistre] = useState(null);    // statut rendu par le serveur
  const [recherche, setRecherche] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [manquants, setManquants] = useState([]);
  const dernierSiren = useRef('');

  const siren = valeurs.siren.replace(/\s/g, '');

  // Recherche au registre dès que le SIREN est plausible, une fois par numéro.
  // Le préremplissage ne remplit QUE les champs vides : il ne défait jamais ce
  // que le client a tapé.
  useEffect(() => {
    if (!sirenPlausible(siren) || siren === dernierSiren.current) return;
    dernierSiren.current = siren;
    let annule = false;
    setRecherche(true); setRegistre(null);
    consulterRegistreEntreprises(siren)
      .then(r => {
        if (annule) return;
        setRegistre(r.statut);
        if (r.statut === 'actif') {
          setValeurs(v => {
            const suite = { ...v };
            ['raison_sociale', 'adresse', 'code_postal', 'ville'].forEach(c => {
              if (!suite[c] && r[c]) suite[c] = r[c];
            });
            return suite;
          });
        }
      })
      .catch(() => { if (!annule) setRegistre('indisponible'); })
      .finally(() => { if (!annule) setRecherche(false); });
    return () => { annule = true; };
  }, [siren]);

  useEffect(() => {
    const echap = (e) => { if (e.key === 'Escape' && !envoi) onClose(); };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, [onClose, envoi]);

  const envoyer = async (e) => {
    e.preventDefault();
    setErreur(''); setManquants([]); setEnvoi(true);
    try {
      const reponse = await putIdentiteFacturation({ ...valeurs, siren });
      await onValide(reponse.identite);
    } catch (err) {
      setErreur(err.message || t('err_generic'));
      setManquants(err.champsManquants || []);
      setEnvoi(false);
    }
  };

  const noteRegistre = recherche ? t('identite_registre_recherche')
    : registre === 'actif' ? t('identite_registre_trouve')
    : registre === 'introuvable' ? t('identite_registre_introuvable')
    : registre === 'cesse' ? t('identite_registre_cesse')
    : null;
  const couleurRegistre = registre === 'cesse' ? '#dc2626'
    : registre === 'introuvable' ? '#b45309' : 'var(--text-muted)';

  const champ = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--gray)',
                  background: 'var(--bg-input)', color: 'var(--black)', fontSize: 14 };
  const libelle = (cle, requis, nom) => (
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4,
                   color: manquants.includes(nom) ? '#dc2626' : 'var(--black)' }}>
      {t(cle)}{requis ? ' *' : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> ({t('identite_facultatif')})</span>}
    </span>
  );

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="identite-titre"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form onSubmit={envoyer}
        style={{ background: 'var(--bg-surface)', borderRadius: 12, padding: 24, width: 560, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' }}>
        <div id="identite-titre" style={{ fontWeight: 700, fontSize: 17, color: 'var(--black)', marginBottom: 4 }}>{t('identite_titre')}</div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px', lineHeight: 1.5 }}>{t('identite_intro')}</p>

        <div role="radiogroup" aria-label={t('identite_profil_label')} style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
          {PROFILS.map(p => (
            <button key={p.cle} type="button" role="radio" aria-checked={profil === p.cle} onClick={() => setProfil(p.cle)}
              style={{ flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                       border: `2px solid ${profil === p.cle ? 'var(--primary, #2563eb)' : 'var(--gray)'}`,
                       background: profil === p.cle ? 'rgba(37,99,235,0.06)' : 'var(--bg-input)' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--black)' }}>🏢 {t(p.titre)}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{t(p.texte)}</div>
            </button>
          ))}
        </div>

        <label style={{ display: 'block', marginBottom: 12 }}>
          {libelle('identite_siren', true, 'siren')}
          <input style={champ} value={valeurs.siren} inputMode="numeric" autoComplete="off" autoFocus
            placeholder="123 456 789" onChange={e => setValeurs(v => ({ ...v, siren: e.target.value }))} />
          <span style={{ display: 'block', fontSize: 12, marginTop: 4, color: couleurRegistre }}>
            {noteRegistre || t('identite_siren_aide')}
          </span>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {CHAMPS.map(([nom, cle, requis]) => (
            <label key={nom} style={nom === 'raison_sociale' || nom === 'adresse' ? { gridColumn: '1 / -1' } : undefined}>
              {libelle(cle, requis, nom)}
              <input style={champ} value={valeurs[nom]}
                type={nom === 'email_facturation' ? 'email' : 'text'}
                placeholder={nom === 'email_facturation' ? emailConnexion : nom === 'tva_intracom' ? 'FR12 123456789' : undefined}
                onChange={e => setValeurs(v => ({ ...v, [nom]: e.target.value }))} />
            </label>
          ))}
          <div>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, color: 'var(--black)' }}>{t('identite_pays')}</span>
            <div style={{ ...champ, color: 'var(--text-muted)' }}>{t('identite_pays_france')}</div>
          </div>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '14px 0 0', lineHeight: 1.5 }}>{t('identite_pourquoi')}</p>
        {erreur && <p role="alert" style={{ fontSize: 13, color: '#dc2626', margin: '10px 0 0' }}>{erreur}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} disabled={envoi}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', cursor: 'pointer' }}>
            {t('identite_annuler')}
          </button>
          <button type="submit" disabled={envoi || registre === 'cesse'}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--primary, #2563eb)', color: '#fff', fontWeight: 600, cursor: envoi ? 'wait' : 'pointer', opacity: registre === 'cesse' ? 0.5 : 1 }}>
            {envoi ? t('identite_envoi') : t(mode === 'paiement' ? 'identite_continuer_paiement' : 'identite_enregistrer')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IdentiteFacturationModal;
